import type { ChangeEvent, ComponentProps, DragEvent, ReactNode, RefObject } from 'react';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { cn } from '@unitfield/ui/lib/utils';
import { DateWell } from '../../shell/date-well';
import { PublicSpinner } from '../../shell/public-loading';
import { CheckRow, ChipChoice, Field, FieldError, TextAreaWell, TextWell, type TextWellProps } from '../../shell/fields';
import shell from '../../shell/shell.module.css';
import { describeMonthly, formatMoney, formatPhone, monthlyIncome, normalizePhone } from './application-card-model';
import styles from './apply.module.css';

/** What a field needs to show its error: the same ids and flags the core computes. */
export interface FieldA11y {
  error: string | null;
  errorId: string;
  invalid: boolean;
  describedBy?: string;
}
export type FieldA11yFor = (field: string) => FieldA11y;

/** The shell's date well at a form field's size: the same height, corner and type as a text well, so dates line up with the fields beside them. */
function ApplyDate({ className, ...props }: ComponentProps<typeof DateWell>) {
  return <DateWell {...props} className={cn(styles.dateField, className)} />;
}

/** What a step says while it has nothing yet: one quiet line, never a drawing (an org host has no illustration assets). */
export function ApplyEmpty({ title, description }: { title: string; description?: string }) {
  return (
    <div className={styles.emptyNote}>
      <p className={styles.emptyTitle}>{title}</p>
      {description ? <p className={styles.emptyText}>{description}</p> : null}
    </div>
  );
}

/* ---------- move-in ---------- */

export function MoveInStep({
  moveIn,
  minDate,
  availability,
  role,
  household,
  showHousehold,
  a11y,
  onMoveIn,
  onBlurMoveIn,
  onRole,
  onHousehold,
}: {
  moveIn: string;
  minDate?: string;
  availability: string | null;
  role: string;
  household: string;
  showHousehold: boolean;
  a11y: FieldA11yFor;
  onMoveIn: (value: string) => void;
  onBlurMoveIn: () => void;
  onRole: (value: 'TENANT' | 'COSIGNER') => void;
  onHousehold: (value: 'ALONE' | 'WITH_CO_APPLICANTS') => void;
}) {
  const date = a11y('expected_move_in_date');
  const roleA11y = a11y('applicant_role');
  const householdA11y = a11y('moving_with');
  return (
    <div className={styles.fields}>
      <Field label="Move-in date" htmlFor="expected_move_in_date" hint={availability} error={date.error} errorId={date.errorId}>
        <ApplyDate id="expected_move_in_date" value={moveIn || null} min={minDate} invalid={date.invalid} describedBy={date.describedBy} onBlur={onBlurMoveIn} onChange={(value) => onMoveIn(value.slice(0, 10))} />
      </Field>
      <div className={styles.field}>
        <p className={styles.label} id="applicant-role-label">You’re applying</p>
        <ChipChoice label="You’re applying" value={role} onChange={onRole} invalid={roleA11y.invalid} describedBy={roleA11y.describedBy}
          options={[['TENANT', 'To live here'], ['COSIGNER', 'As a co-signer']]} />
        <FieldError id={roleA11y.errorId} message={roleA11y.error} />
      </div>
      {showHousehold ? (
        <div className={styles.field}>
          <p className={styles.label}>Moving in</p>
          <ChipChoice label="Moving in" value={household} onChange={onHousehold} invalid={householdA11y.invalid} describedBy={householdA11y.describedBy}
            options={[['ALONE', 'Alone'], ['WITH_CO_APPLICANTS', 'With others']]} />
          <FieldError id={householdA11y.errorId} message={householdA11y.error} />
        </div>
      ) : null}
    </div>
  );
}

/* ---------- about you ---------- */

export function PhoneWell({ id, value, onValueChange, invalid, describedBy, onBlur }: {
  id: string; value: string; onValueChange: (value: string) => void; invalid?: boolean; describedBy?: string; onBlur?: () => void;
}) {
  const shown = value.startsWith('+') ? value : value.replace(/\D/g, '').length <= 10 ? formatPhone(value) || value : value;
  return (
    <TextWell id={id} type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" className="num"
      value={shown} invalid={invalid} aria-describedby={describedBy} onBlur={onBlur}
      onChange={(event) => onValueChange(normalizePhone(event.target.value))} />
  );
}

export interface AboutYouValues { first_name: string; last_name: string; email: string; phone: string; date_of_birth: string }

/** The fields the applicant types here. The email is not one of them: it is the address that was verified at sign-in. */
export type AboutYouEditable = Exclude<keyof AboutYouValues, 'email'>;

export function AboutYouStep({ values, a11y, onChange, onTouch, maxBirthDate, onUseDifferentEmail }: {
  values: AboutYouValues;
  a11y: FieldA11yFor;
  onChange: (field: AboutYouEditable, value: string) => void;
  onTouch: (field: AboutYouEditable) => void;
  maxBirthDate: string;
  /** Signs out and restarts verification for another address. The applicant never edits the verified address in place. */
  onUseDifferentEmail: () => void;
}) {
  const text = (field: 'first_name' | 'last_name', label: string, props: Partial<TextWellProps> = {}) => {
    const state = a11y(field);
    return (
      <Field label={label} htmlFor={field} error={state.error} errorId={state.errorId}>
        <TextWell id={field} value={values[field]} invalid={state.invalid} aria-describedby={state.describedBy}
          onChange={(event) => onChange(field, event.target.value)} onBlur={() => onTouch(field)} {...props} />
      </Field>
    );
  };
  const phone = a11y('phone');
  const birth = a11y('date_of_birth');
  return (
    <div className={styles.fields}>
      <div className={styles.pair}>
        {text('first_name', 'First name', { autoComplete: 'given-name' })}
        {text('last_name', 'Last name', { autoComplete: 'family-name' })}
      </div>
      <Field
        label="Email"
        htmlFor="applicant_email"
        hint={<>You verified this address. <button type="button" className={styles.textLink} onClick={onUseDifferentEmail}>Use a different email</button></>}
      >
        <TextWell id="applicant_email" type="email" value={values.email} readOnly aria-readonly="true" autoComplete="email" inputMode="email" wellClassName={styles.verifiedWell} />
      </Field>
      <div className={styles.pair}>
        <Field label="Phone" htmlFor="applicant_phone" error={phone.error} errorId={phone.errorId}>
          <PhoneWell id="applicant_phone" value={values.phone} invalid={phone.invalid} describedBy={phone.describedBy}
            onValueChange={(value) => onChange('phone', value)} onBlur={() => onTouch('phone')} />
        </Field>
        <Field label="Date of birth" htmlFor="date_of_birth" hint={birth.error ? null : 'You must be 18 or older.'} error={birth.error} errorId={birth.errorId}>
          <ApplyDate id="date_of_birth" value={values.date_of_birth || null} max={maxBirthDate} range="past" placeholder="Pick your birthday" invalid={birth.invalid}
            describedBy={birth.describedBy} onBlur={() => onTouch('date_of_birth')} onChange={(value) => onChange('date_of_birth', value.slice(0, 10))} />
        </Field>
      </div>
    </div>
  );
}

/* ---------- saved items: jobs and homes ---------- */

function SavedRow({ icon, title, detail, flagged, onEdit, onRemove, editLabel, removeLabel }: {
  icon: string; title: string; detail: string | null; flagged?: boolean; onEdit: () => void; onRemove?: () => void; editLabel: string; removeLabel: string;
}) {
  return (
    <li className={styles.saved}>
      <span className={styles.tile}><UnitfieldIcon name={icon} size={17} aria-hidden="true" /></span>
      <span className={styles.savedText}>
        <span className={styles.savedTitle}>{title}</span>
        {detail ? <span className={cn(styles.savedDetail, 'num')} data-flagged={flagged ? '' : undefined}>{detail}</span> : null}
      </span>
      <span className={styles.savedActions}>
        <button type="button" className={styles.textLink} onClick={onEdit} aria-label={editLabel}>Edit</button>
        {onRemove ? <button type="button" className={styles.textLink} onClick={onRemove} aria-label={removeLabel}>Remove</button> : null}
      </span>
    </li>
  );
}

export interface JobValues {
  job_type: string;
  annual_income: number | '';
  title: string;
  company_name: string;
  currently_work_here: boolean;
  start_date: string;
  end_date: string;
  supervisor_name: string;
  supervisor_email: string;
  supervisor_phone: string;
}

const jobTypeOptions = [
  ['Employed', 'Employed'], ['SelfEmployed', 'Self-employed'], ['Student', 'Student'], ['Unemployed', 'Not working'], ['Other', 'Other'],
] as const;

export function jobSummary(job: JobValues): { title: string; detail: string | null } {
  const label = jobTypeOptions.find(([value]) => value === job.job_type)?.[1] ?? 'Job';
  const title = [job.title.trim(), job.company_name.trim()].filter(Boolean).join(', ') || label;
  const month = monthlyIncome(job.annual_income);
  return { title, detail: month === null ? null : `${formatMoney(month)} / mo` };
}

export function EmploymentStep({
  jobs,
  openIndex,
  isNew,
  invalidIndexes,
  showSupervisor,
  a11y,
  onUpdate,
  onTouch,
  onOpen,
  onAdd,
  onCancel,
  onRemove,
  onShowSupervisor,
  onHideSupervisor,
}: {
  jobs: JobValues[];
  openIndex: number | null;
  isNew: boolean;
  invalidIndexes: ReadonlySet<number>;
  showSupervisor: boolean;
  a11y: FieldA11yFor;
  onUpdate: (index: number, patch: Partial<JobValues>) => void;
  onTouch: (field: string) => void;
  onOpen: (index: number) => void;
  onAdd: () => void;
  onCancel: () => void;
  onRemove: (index: number) => void;
  onShowSupervisor: () => void;
  /** Closes the reference and clears what was typed in it. */
  onHideSupervisor: () => void;
}) {
  const saved = jobs.map((job, index) => ({ job, index })).filter(({ index }) => index !== openIndex);
  const open = openIndex === null ? null : jobs[openIndex];
  return (
    <div className={styles.fields}>
      {saved.length > 0 ? (
        <ul className={styles.savedList} aria-label="Your jobs">
          {saved.map(({ job, index }) => {
            const summary = jobSummary(job);
            const flagged = invalidIndexes.has(index);
            return (
              <SavedRow key={index} icon="briefcase" title={summary.title} detail={flagged ? 'Needs a few details' : summary.detail} flagged={flagged}
                onEdit={() => onOpen(index)} onRemove={jobs.length > 1 ? () => onRemove(index) : undefined}
                editLabel={`Edit ${summary.title}`} removeLabel={`Remove ${summary.title}`} />
            );
          })}
        </ul>
      ) : null}
      {open && openIndex !== null ? (
        <JobForm index={openIndex} job={open} isNew={isNew} showSupervisor={showSupervisor} a11y={a11y} onUpdate={onUpdate} onTouch={onTouch} onCancel={onCancel} onShowSupervisor={onShowSupervisor} onHideSupervisor={onHideSupervisor} />
      ) : (
        <button type="button" className={cn(shell.pill, styles.addPill)} onClick={onAdd}>
          <UnitfieldIcon name="plus" size={16} aria-hidden="true" />Add another job
        </button>
      )}
    </div>
  );
}

function JobForm({ index, job, isNew, showSupervisor, a11y, onUpdate, onTouch, onCancel, onShowSupervisor, onHideSupervisor }: {
  index: number; job: JobValues; isNew: boolean; showSupervisor: boolean; a11y: FieldA11yFor;
  onUpdate: (index: number, patch: Partial<JobValues>) => void; onTouch: (field: string) => void; onCancel: () => void; onShowSupervisor: () => void; onHideSupervisor: () => void;
}) {
  const key = (field: keyof JobValues) => `employment.${index}.${field}`;
  const id = (field: keyof JobValues) => `employment-${index}-${field}`;
  const employed = job.job_type === 'Employed' || job.job_type === 'SelfEmployed';
  const type = a11y(key('job_type'));
  const income = a11y(key('annual_income'));
  const title = a11y(key('title'));
  const company = a11y(key('company_name'));
  const start = a11y(key('start_date'));
  const end = a11y(key('end_date'));
  const monthly = describeMonthly(job.annual_income);
  return (
    <div className={styles.itemForm} role="group" aria-label={isNew ? 'Add a job' : 'Edit this job'}>
      <div className={styles.field}>
        <ChipChoice label="Kind of work" value={job.job_type} options={jobTypeOptions} invalid={type.invalid} describedBy={type.describedBy}
          onChange={(value) => onUpdate(index, { job_type: value })} />
        <FieldError id={type.errorId} message={type.error} />
      </div>
      {employed ? (
        <>
          <Field label="Job title" htmlFor={id('title')} error={title.error} errorId={title.errorId}>
            <TextWell id={id('title')} value={job.title} invalid={title.invalid} aria-describedby={title.describedBy}
              onChange={(event) => onUpdate(index, { title: event.target.value })} onBlur={() => onTouch(key('title'))} />
          </Field>
          <Field label={job.job_type === 'SelfEmployed' ? 'Business name' : 'Employer'} htmlFor={id('company_name')} error={company.error} errorId={company.errorId}>
            <TextWell id={id('company_name')} value={job.company_name} invalid={company.invalid} aria-describedby={company.describedBy}
              onChange={(event) => onUpdate(index, { company_name: event.target.value })} onBlur={() => onTouch(key('company_name'))} />
          </Field>
        </>
      ) : null}
      <Field label="Yearly income before taxes" htmlFor={id('annual_income')} hint={income.error ? null : monthly} error={income.error} errorId={income.errorId}>
        <TextWell id={id('annual_income')} leading="$" inputMode="numeric" className="num" placeholder="e.g. 60,000"
          value={job.annual_income === '' ? '' : String(job.annual_income)} invalid={income.invalid} aria-describedby={income.describedBy}
          onChange={(event) => {
            const next = event.target.value.replace(/[^\d.]/g, '').trim();
            onUpdate(index, { annual_income: next ? Number(next) : '' });
          }}
          onBlur={() => onTouch(key('annual_income'))} />
      </Field>
      {employed ? (
        <>
          <CheckRow checked={job.currently_work_here} onChange={(checked) => onUpdate(index, { currently_work_here: checked })}>
            I still work here
          </CheckRow>
          <div className={styles.pair}>
            <Field label="Started" htmlFor={id('start_date')} error={start.error} errorId={start.errorId}>
              <ApplyDate id={id('start_date')} value={job.start_date || null} range="past" invalid={start.invalid} describedBy={start.describedBy}
                onBlur={() => onTouch(key('start_date'))} onChange={(value) => onUpdate(index, { start_date: value.slice(0, 10) })} />
            </Field>
            {!job.currently_work_here ? (
              <Field label="Ended" htmlFor={id('end_date')} error={end.error} errorId={end.errorId}>
                <ApplyDate id={id('end_date')} value={job.end_date || null} range="past" invalid={end.invalid} describedBy={end.describedBy}
                  onBlur={() => onTouch(key('end_date'))} onChange={(value) => onUpdate(index, { end_date: value.slice(0, 10) })} />
              </Field>
            ) : null}
          </div>
        </>
      ) : null}
      {job.job_type ? showSupervisor ? (
        <div className={styles.subFields} role="group" aria-label="Someone who can confirm this job">
          <div className={styles.subHead}>
            <p className={styles.label}>Someone who can confirm this job <span className={styles.optional}>Optional</span></p>
            <button type="button" className={styles.textLink} onClick={onHideSupervisor}>Remove</button>
          </div>
          <Field label="Name" htmlFor={id('supervisor_name')}>
            <TextWell id={id('supervisor_name')} value={job.supervisor_name} onChange={(event) => onUpdate(index, { supervisor_name: event.target.value })} />
          </Field>
          <div className={styles.pair}>
            <Field label="Email" htmlFor={id('supervisor_email')}>
              <TextWell id={id('supervisor_email')} type="email" value={job.supervisor_email} onChange={(event) => onUpdate(index, { supervisor_email: event.target.value })} />
            </Field>
            <Field label="Phone" htmlFor={id('supervisor_phone')}>
              <PhoneWell id={id('supervisor_phone')} value={job.supervisor_phone} onValueChange={(value) => onUpdate(index, { supervisor_phone: value })} />
            </Field>
          </div>
        </div>
      ) : (
        <button type="button" className={cn(styles.textLink, styles.inlineAdd)} onClick={onShowSupervisor}>
          Add someone who can confirm this job
        </button>
      ) : null}
      {isNew ? (
        <button type="button" className={cn(shell.btn, shell.btnTonal, shell.btnSmall, styles.cancel)} onClick={onCancel}>Cancel</button>
      ) : null}
    </div>
  );
}

export interface HomeValues {
  occupancy_type: string;
  address_line_1: string;
  city: string;
  state: string;
  move_in_date: string;
  move_out_date: string;
}

export function ResidencesStep({
  homes,
  openIndex,
  isNew,
  invalidIndexes,
  empty,
  a11y,
  onUpdate,
  onTouch,
  onOpen,
  onAdd,
  onCancel,
  onRemove,
}: {
  homes: HomeValues[];
  openIndex: number | null;
  isNew: boolean;
  invalidIndexes: ReadonlySet<number>;
  /** Shown when nothing is added yet. */
  empty: ReactNode;
  a11y: FieldA11yFor;
  onUpdate: (index: number, patch: Partial<HomeValues>) => void;
  onTouch: (field: string) => void;
  onOpen: (index: number) => void;
  onAdd: () => void;
  onCancel: () => void;
  onRemove: (index: number) => void;
}) {
  const saved = homes.map((home, index) => ({ home, index })).filter(({ index }) => index !== openIndex);
  const open = openIndex === null ? null : homes[openIndex];
  return (
    <div className={styles.fields}>
      {saved.length > 0 ? (
        <ul className={styles.savedList} aria-label="Your past homes">
          {saved.map(({ home, index }) => {
            const title = home.address_line_1.trim() || home.city.trim() || 'Home';
            const flagged = invalidIndexes.has(index);
            const detail = flagged ? 'Needs a few details' : [home.city.trim() && home.address_line_1.trim() ? home.city.trim() : null, home.occupancy_type === 'Own' ? 'Owned' : home.occupancy_type === 'Rent' ? 'Rented' : null].filter(Boolean).join(' · ');
            return (
              <SavedRow key={index} icon="home" title={title} detail={detail || null} flagged={flagged} onEdit={() => onOpen(index)} onRemove={() => onRemove(index)}
                editLabel={`Edit ${title}`} removeLabel={`Remove ${title}`} />
            );
          })}
        </ul>
      ) : null}
      {open && openIndex !== null ? (
        <HomeForm index={openIndex} home={open} isNew={isNew} a11y={a11y} onUpdate={onUpdate} onTouch={onTouch} onCancel={onCancel} />
      ) : (
        <>
          {homes.length === 0 ? <div className={styles.empty}>{empty}</div> : null}
          <button type="button" className={cn(shell.pill, styles.addPill)} onClick={onAdd}>
            <UnitfieldIcon name="plus" size={16} aria-hidden="true" />{homes.length === 0 ? 'Add a home' : 'Add another home'}
          </button>
        </>
      )}
    </div>
  );
}

function HomeForm({ index, home, isNew, a11y, onUpdate, onTouch, onCancel }: {
  index: number; home: HomeValues; isNew: boolean; a11y: FieldA11yFor;
  onUpdate: (index: number, patch: Partial<HomeValues>) => void; onTouch: (field: string) => void; onCancel: () => void;
}) {
  const key = (field: keyof HomeValues) => `residence.${index}.${field}`;
  const id = (field: keyof HomeValues) => `residence-${index}-${field}`;
  const address = a11y(key('address_line_1'));
  const city = a11y(key('city'));
  const state = a11y(key('state'));
  const moveIn = a11y(key('move_in_date'));
  return (
    <div className={styles.itemForm} role="group" aria-label={isNew ? 'Add a home' : 'Edit this home'}>
      <ChipChoice label="Did you rent or own it?" value={home.occupancy_type} onChange={(value) => onUpdate(index, { occupancy_type: value })}
        options={[['Rent', 'Rented'], ['Own', 'Owned'], ['Other', 'Other']]} />
      <Field label="Street address" htmlFor={id('address_line_1')} error={address.error} errorId={address.errorId}>
        <TextWell id={id('address_line_1')} autoComplete="street-address" value={home.address_line_1} invalid={address.invalid} aria-describedby={address.describedBy}
          onChange={(event) => onUpdate(index, { address_line_1: event.target.value })} onBlur={() => onTouch(key('address_line_1'))} />
      </Field>
      <div className={styles.pair} data-ratio="wide">
        <Field label="City" htmlFor={id('city')} error={city.error} errorId={city.errorId}>
          <TextWell id={id('city')} value={home.city} invalid={city.invalid} aria-describedby={city.describedBy}
            onChange={(event) => onUpdate(index, { city: event.target.value })} onBlur={() => onTouch(key('city'))} />
        </Field>
        <Field label="State" htmlFor={id('state')} error={state.error} errorId={state.errorId}>
          <TextWell id={id('state')} value={home.state} invalid={state.invalid} aria-describedby={state.describedBy}
            onChange={(event) => onUpdate(index, { state: event.target.value })} onBlur={() => onTouch(key('state'))} />
        </Field>
      </div>
      <div className={styles.pair}>
        <Field label="Moved in" htmlFor={id('move_in_date')} error={moveIn.error} errorId={moveIn.errorId}>
          <ApplyDate id={id('move_in_date')} value={home.move_in_date || null} range="past" invalid={moveIn.invalid} describedBy={moveIn.describedBy}
            onBlur={() => onTouch(key('move_in_date'))} onChange={(value) => onUpdate(index, { move_in_date: value.slice(0, 10) })} />
        </Field>
        <Field label="Moved out" htmlFor={id('move_out_date')} optional>
          <ApplyDate id={id('move_out_date')} value={home.move_out_date || null} range="past" placeholder="Still there" onChange={(value) => onUpdate(index, { move_out_date: value.slice(0, 10) })} />
        </Field>
      </div>
      {isNew ? (
        <button type="button" className={cn(shell.btn, shell.btnTonal, shell.btnSmall, styles.cancel)} onClick={onCancel}>Cancel</button>
      ) : null}
    </div>
  );
}

/* ---------- questions ---------- */

export interface QuestionView {
  id: string;
  label: string;
  type: string;
  required: boolean;
  options: string[];
}

export type QuestionAnswer = string | number | boolean | string[] | null;

export function CustomQuestion({ question, value, a11y, onAnswer, onToggle, onTouch }: {
  question: QuestionView;
  value: QuestionAnswer;
  a11y: FieldA11y;
  onAnswer: (value: QuestionAnswer) => void;
  onToggle: (option: string, checked: boolean) => void;
  onTouch: () => void;
}) {
  const id = `custom-question-${question.id}`;
  const type = question.type.toLowerCase();
  const text = typeof value === 'string' ? value : '';
  const label = <>{question.label}{question.required ? null : <span className={styles.optional}>Optional</span>}</>;

  if (type === 'yesno' || type === 'singleselect' || type === 'multiselect') {
    const options: ReadonlyArray<readonly [string, string]> = type === 'yesno'
      ? [['true', 'Yes'], ['false', 'No']]
      : question.options.map((option) => [option, option] as const);
    return (
      <div className={styles.field}>
        <p className={styles.label}>{label}</p>
        {type === 'multiselect' ? (
          <div className={styles.checks} role="group" aria-label={question.label}>
            {question.options.map((option) => (
              <CheckRow key={option} checked={Array.isArray(value) && value.includes(option)} onChange={(checked) => onToggle(option, checked)}>{option}</CheckRow>
            ))}
          </div>
        ) : (
          <ChipChoice label={question.label} invalid={a11y.invalid} describedBy={a11y.describedBy} options={options}
            value={type === 'yesno' ? (typeof value === 'boolean' ? String(value) : '') : text}
            onChange={(next) => onAnswer(type === 'yesno' ? next === 'true' : next)} />
        )}
        <FieldError id={a11y.errorId} message={a11y.error} />
      </div>
    );
  }

  return (
    <Field label={label} htmlFor={id} error={a11y.error} errorId={a11y.errorId}>
      {type === 'longtext' ? (
        <TextAreaWell id={id} value={text} invalid={a11y.invalid} aria-describedby={a11y.describedBy} onBlur={onTouch} onChange={(event) => onAnswer(event.target.value)} />
      ) : type === 'number' ? (
        <TextWell id={id} inputMode="decimal" className="num" value={typeof value === 'number' ? String(value) : ''} invalid={a11y.invalid} aria-describedby={a11y.describedBy} onBlur={onTouch}
          onChange={(event) => {
            const next = event.target.value.replace(/[^\d.-]/g, '').trim();
            onAnswer(next && Number.isFinite(Number(next)) ? Number(next) : null);
          }} />
      ) : type === 'date' || type === 'string' ? (
        <ApplyDate id={id} value={text || null} invalid={a11y.invalid} describedBy={a11y.describedBy} onBlur={onTouch} onChange={(next) => onAnswer(next.slice(0, 10))} />
      ) : (
        <TextWell id={id} value={text} invalid={a11y.invalid} aria-describedby={a11y.describedBy} onBlur={onTouch} onChange={(event) => onAnswer(event.target.value)} />
      )}
    </Field>
  );
}

/* ---------- documents ---------- */

export interface DocumentRow { id: string; name: string; type: string; uploaded: string; icon: string }

export function DocumentsStep({
  category,
  categories,
  onCategory,
  uploading,
  uploadingName,
  dragging,
  error,
  documents,
  empty,
  inputRef,
  accept,
  onInputChange,
  onDragState,
  onDrop,
}: {
  category: string;
  categories: ReadonlyArray<readonly [string, string]>;
  onCategory: (value: string) => void;
  uploading: boolean;
  uploadingName: string;
  dragging: boolean;
  error: string | null;
  documents: DocumentRow[];
  empty: ReactNode;
  inputRef: RefObject<HTMLInputElement | null>;
  accept: string;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDragState: (dragging: boolean) => void;
  onDrop: (event: DragEvent<HTMLButtonElement>) => void;
}) {
  return (
    <div className={styles.fields}>
      <div className={styles.field}>
        <p className={styles.label} id="attachment-category">What are you adding?</p>
        <ChipChoice label="What are you adding?" value={category} options={categories} onChange={onCategory} disabled={uploading} />
      </div>
      <input ref={inputRef} id="attachment-upload" type="file" className="sr-only" accept={accept} onChange={onInputChange} tabIndex={-1} aria-hidden="true" />
      <button
        type="button"
        className={styles.drop}
        data-dragging={dragging ? '' : undefined}
        data-busy={uploading ? '' : undefined}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => { event.preventDefault(); if (!uploading) onDragState(true); }}
        onDragOver={(event) => { event.preventDefault(); if (!uploading) onDragState(true); }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (!event.currentTarget.contains(event.relatedTarget as Node)) onDragState(false);
        }}
        onDrop={onDrop}
        aria-describedby="attachment-upload-help"
        aria-busy={uploading}
        disabled={uploading}
      >
        <span className={styles.dropTile}>
          {uploading ? <PublicSpinner size={20} decorative /> : <UnitfieldIcon name="upload" size={20} aria-hidden="true" />}
        </span>
        <span className={styles.dropText}>
          <b>{uploading ? 'Uploading…' : 'Add a file'}</b>
          <span id="attachment-upload-help">{uploading ? uploadingName : 'PDF, Word, JPG, PNG or WebP, up to 10 MB'}</span>
        </span>
      </button>
      {error ? (
        <p role="alert" className={styles.errorLine}>
          <UnitfieldIcon name="warning-circle" size={16} aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : null}
      {documents.length === 0 ? (
        <div className={styles.empty}>{empty}</div>
      ) : (
        <ul className={styles.savedList} aria-label="Uploaded documents">
          {documents.map((document) => (
            <li key={document.id} className={styles.saved}>
              <span className={styles.tile}><UnitfieldIcon name={document.icon} size={17} aria-hidden="true" /></span>
              <span className={styles.savedText}>
                <span className={styles.savedTitle}>{document.name}</span>
                <span className={cn(styles.savedDetail, 'num')}>{document.type} · {document.uploaded}</span>
              </span>
              <span className={styles.uploaded}><UnitfieldIcon name="check" size={13} strokeWidth={2.6} aria-hidden="true" />Uploaded</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- sign and send ---------- */

export function SignStep({
  certified,
  onCertified,
  certificationText,
  termsUrl,
  signature,
  onSignature,
  disabled,
  screeningNotice,
}: {
  certified: boolean;
  onCertified: (checked: boolean) => void;
  certificationText: string;
  termsUrl: string;
  signature: string;
  onSignature: (value: string) => void;
  disabled: boolean;
  screeningNotice: string | null;
}) {
  return (
    <div className={styles.fields}>
      <CheckRow checked={certified} disabled={disabled} onChange={onCertified}>
        {certificationText}{' '}
        <a href={termsUrl} target="_blank" rel="noreferrer">Rental Application Terms</a>.
      </CheckRow>
      <Field label="Type your full legal name to sign" htmlFor="application-signature">
        <TextWell id="application-signature" autoComplete="name" className={styles.signature} placeholder="Your full name" value={signature} disabled={disabled}
          onChange={(event) => onSignature(event.target.value)} />
      </Field>
      {screeningNotice ? <p className={styles.note}>{screeningNotice}</p> : null}
    </div>
  );
}
